// src/pages/Historico/GrowthCurveModal.jsx
// Modal na página Amostras: recebe os experimentos selecionados (réplicas) e
// monta a curva de crescimento sigmoidal consolidada. Abas por métrica
// (Turbidimetria/Nefelometria/Fluorescência), buscando SÓ a métrica ativa sob
// demanda (lazy + cache via useGrowthSeries) e mostrando estado de loading.
// Molde: BasicChartModal (ExperimentsPage) + AnaliseDetalhesModal (fullScreen mobile).
import React, { useMemo, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Stack,
    Tabs,
    Tab,
    Typography,
    CircularProgress,
    LinearProgress,
    Alert,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    ToggleButtonGroup,
    ToggleButton,
    Tooltip,
    Checkbox,
    FormControlLabel,
    TextField,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import * as Lucide from 'lucide-react';

import { GraficoTipo } from '../../infra/api/GraficoApi';
import { useGrowthSeries } from '../../hooks/useGrowthSeries';
import {
    buildGrowthCurve,
    availableChannels,
    DEFAULT_CHANNEL,
} from '../../utils/growthCurve';
import GrowthCurveChart from '../../components/GrowthCurveChart';

const METRIC_TABS = [
    { key: GraficoTipo.SPECTRAL_TURBIDIMETRY, label: 'Turbidimetria' },
    { key: GraficoTipo.SPECTRAL_NEPHELOMETRY, label: 'Nefelometria' },
    { key: GraficoTipo.SPECTRAL_FLUORESCENCE, label: 'Fluorescência' },
];

// Canais espectrais oferecidos (AS7341). Default F2 = mesmo do BasicChartModal.
const CHANNEL_OPTIONS = ['F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'CLEAR', 'NIR'];

const replicaLabel = (exp, i) =>
    exp?.nome ||
    exp?.tag ||
    exp?.serialNumber ||
    exp?.deviceName ||
    `Réplica ${i + 1}`;

export default function GrowthCurveModal({ open, experiments = [], onClose }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [activeMetric, setActiveMetric] = useState(GraficoTipo.SPECTRAL_TURBIDIMETRY);
    const [channel, setChannel] = useState(DEFAULT_CHANNEL);
    const [mode, setMode] = useState('raw'); // 'raw' | 'basic'
    // Suavização SÓ DE EXIBIÇÃO (não toca no TTD/fases, que seguem no sinal cru).
    const [smoothDisplay, setSmoothDisplay] = useState(false);
    const [smoothWindow, setSmoothWindow] = useState(30); // min

    const ids = useMemo(
        () => experiments.map((e) => String(e.id)).filter(Boolean),
        [experiments]
    );

    const { seriesByExp, loading, progress, errors } = useGrowthSeries(
        ids,
        activeMetric,
        open
    );

    const errorList = useMemo(() => Object.values(errors || {}), [errors]);

    // Canais efetivamente presentes nos dados carregados (para não oferecer canal vazio).
    const channelsInData = useMemo(() => {
        const first = Object.values(seriesByExp)[0];
        const chs = first ? availableChannels(first) : [];
        return chs.length ? chs : CHANNEL_OPTIONS;
    }, [seriesByExp]);

    // Ganho + tempo de integração por experimento p/ a métrica ativa (Basic Count).
    // metricKey: turbidimetry | nephelometry | fluorescence
    const metricKey = activeMetric.toLowerCase().split('_')[1];
    const items = useMemo(
        () =>
            experiments
                .map((e, i) => {
                    const sp = e?.spectral || {};
                    // nef/fluo podem herdar a config da turbidimetria (mesma convenção do ColorConversionApi)
                    const cfg = sp[metricKey] || sp.turbidimetry || {};
                    const ref = cfg.reference || cfg.measurement || cfg || {};
                    const gain = ref?.gain ?? cfg?.gain;
                    const tint =
                        ref?.integrationTime ??
                        ref?.timeMs ??
                        cfg?.integrationTime ??
                        cfg?.timeMs;
                    return {
                        id: String(e.id),
                        label: replicaLabel(e, i),
                        chart: seriesByExp[String(e.id)],
                        gain: gain != null ? Number(gain) : null,
                        tint: tint != null ? Number(tint) : null,
                    };
                })
                .filter((it) => it.chart),
        [experiments, seriesByExp, metricKey]
    );

    const hasGainTint = useMemo(() => items.some((it) => it.gain && it.tint), [items]);
    const effectiveMode = mode === 'basic' && hasGainTint ? 'basic' : 'raw';
    const yUnitLabel = effectiveMode === 'basic' ? 'Basic Count' : 'RAW';

    const curve = useMemo(() => {
        if (loading) return null;
        if (items.length === 0) return { hasData: false };
        return buildGrowthCurve(items, channel, { mode: effectiveMode });
    }, [items, loading, channel, effectiveMode]);

    const activeLabel = METRIC_TABS.find((t) => t.key === activeMetric)?.label || '';
    const replicasComDados = curve?.replicas?.length ?? 0;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    height: isMobile ? '100%' : '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: isMobile ? 0 : 3,
                },
            }}
        >
            <DialogTitle
                sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                    py: 1.5,
                }}
            >
                <Stack direction="row" alignItems="center" gap={1}>
                    <Lucide.ChartSpline />
                    <Box>
                        <Typography variant="h6" component="div" fontWeight={600} lineHeight={1.1}>
                            Curva de crescimento
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.85 }}>
                            {experiments.length} experimento(s) selecionado(s)
                        </Typography>
                    </Box>
                </Stack>
                <IconButton onClick={onClose} sx={{ color: 'inherit' }}>
                    <Lucide.X />
                </IconButton>
            </DialogTitle>

            {/* Cabeçalho de controles em DUAS linhas: abas (scrollable) em cima e
                unidade (RAW/Basic) + canal embaixo. Evita overflow em tablet/mobile,
                e o padding-top da 2ª linha dá folga p/ o label flutuante "Canal"
                não ser cortado pelo cabeçalho. */}
            <Box sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={activeMetric}
                    onChange={(_, v) => setActiveMetric(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    sx={{ minHeight: 44, px: { xs: 0.5, sm: 1 } }}
                >
                    {METRIC_TABS.map((tab) => (
                        <Tab key={tab.key} label={tab.label} value={tab.key} sx={{ minHeight: 44 }} />
                    ))}
                </Tabs>

                <Stack
                    direction="row"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{
                        alignItems: 'center',
                        justifyContent: { xs: 'space-between', sm: 'flex-end' },
                        gap: 1,
                        px: { xs: 1, sm: 1.5 },
                        pt: 1.5,
                        pb: 1,
                    }}
                >
                    <Tooltip
                        title={
                            hasGainTint
                                ? ''
                                : 'Basic Count indisponível: sem ganho/tempo de integração nesta métrica'
                        }
                    >
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={effectiveMode}
                            onChange={(_, v) => v && setMode(v)}
                        >
                            <ToggleButton
                                value="raw"
                                sx={{ px: 1.2, py: 0.3, fontSize: '0.72rem', textTransform: 'none' }}
                            >
                                RAW
                            </ToggleButton>
                            <ToggleButton
                                value="basic"
                                disabled={!hasGainTint}
                                sx={{ px: 1.2, py: 0.3, fontSize: '0.72rem', textTransform: 'none' }}
                            >
                                Basic Count
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Tooltip>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Canal</InputLabel>
                        <Select
                            label="Canal"
                            value={channelsInData.includes(channel) ? channel : channelsInData[0]}
                            onChange={(e) => setChannel(e.target.value)}
                        >
                            {channelsInData.map((ch) => (
                                <MenuItem key={ch} value={ch}>
                                    {ch}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Tooltip title="Suaviza apenas as linhas do gráfico (média móvel centrada). NÃO altera o TTD nem as fases, que seguem no sinal cru.">
                        <FormControlLabel
                            sx={{ mr: 0 }}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={smoothDisplay}
                                    onChange={(e) => setSmoothDisplay(e.target.checked)}
                                />
                            }
                            label={
                                <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>
                                    Suavizar (exibição)
                                </Typography>
                            }
                        />
                    </Tooltip>
                    {smoothDisplay && (
                        <TextField
                            size="small"
                            type="number"
                            label="Janela (min)"
                            value={smoothWindow}
                            onChange={(e) => {
                                const v = parseInt(e.target.value, 10);
                                setSmoothWindow(Number.isFinite(v) && v > 0 ? v : 1);
                            }}
                            inputProps={{ min: 1, step: 5, style: { width: 56 } }}
                            sx={{ width: 108 }}
                        />
                    )}
                </Stack>
            </Box>

            <DialogContent
                dividers
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    p: { xs: 1, sm: 1.5 },
                    gap: 1,
                }}
            >
                {experiments.length < 2 && (
                    <Alert severity="info" variant="outlined" sx={{ flexShrink: 0 }}>
                        Selecione 2 ou mais experimentos para ver média das réplicas e banda ±1 DP.
                    </Alert>
                )}
                {errorList.length > 0 && !loading && (
                    <Alert severity="warning" variant="outlined" sx={{ flexShrink: 0 }}>
                        {errorList.length} experimento(s) não puderam ser carregados nesta métrica.
                    </Alert>
                )}

                {loading ? (
                    <Stack sx={{ flex: 1 }} alignItems="center" justifyContent="center" spacing={2}>
                        <CircularProgress />
                        <Typography color="text.secondary">
                            Calculando curva… {progress.done}/{progress.total} experimentos
                        </Typography>
                        <Box sx={{ width: '60%', maxWidth: 320 }}>
                            <LinearProgress
                                variant="determinate"
                                value={progress.total ? (progress.done / progress.total) * 100 : 0}
                            />
                        </Box>
                    </Stack>
                ) : (
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        <GrowthCurveChart
                            curve={curve}
                            metricLabel={activeLabel}
                            yUnitLabel={yUnitLabel}
                            smoothing={{ enabled: smoothDisplay, windowMin: smoothWindow }}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ flexShrink: 0, justifyContent: 'space-between', px: 2, py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                    {replicasComDados > 0
                        ? `${replicasComDados} réplica(s) com dados em ${activeLabel}`
                        : '—'}
                </Typography>
                <Button onClick={onClose} variant="contained" disableElevation>
                    Fechar
                </Button>
            </DialogActions>
        </Dialog>
    );
}
